import { useState } from "react";
import { Button, Form } from "react-bootstrap";
import RequiredReactSelect from "@components/FormsV2/RequiredReactSelect/RequiredReactSelect";
import { ItemPicker } from "./ItemPicker";
import { ConditionFields, type ConditionValue } from "./ConditionFields";
import { RefinementsEditor } from "./RefinementsEditor";
import { useServiceSubservices } from "./api/useServiceSubservices";
import {
  type ItemOption,
  type ItemType,
  type Operand,
  type RecommendationFormValues,
} from "./types";
import "./style.css";

export interface RecommendationFormProps {
  mode: "create" | "edit";
  initialValues: RecommendationFormValues;
  operands: Operand[];
  services: ItemOption[];
  governances: ItemOption[];
  products: ItemOption[];
  onCancel: () => void;
  onSave: (values: RecommendationFormValues) => void;
}

export const RecommendationForm = ({
  mode,
  initialValues,
  operands,
  services,
  governances,
  products,
  onCancel,
  onSave,
}: RecommendationFormProps): JSX.Element => {
  const [values, setValues] = useState<RecommendationFormValues>(initialValues);
  const set = <K extends keyof RecommendationFormValues>(
    k: K,
    v: RecommendationFormValues[K]
  ) => setValues((prev) => ({ ...prev, [k]: v }));

  const { subservices } = useServiceSubservices(
    values.itemType === "Task" && values.itemId ? values.itemId : null
  );

  const operandOptions = operands.map((o) => ({
    value: o.slug,
    label: o.name,
  }));

  const conditionComplete =
    values.conditionType === "always" ||
    (values.conditionalValue !== "" && values.comparisonValue !== "");
  const valid =
    values.quantityValue !== "" && values.itemId !== "" && conditionComplete;

  return (
    <div className="recommendations-tab-card">
      <div className="recommendations-tab-card__header">
        <div className="recommendations-tab-card__title-group">
          <Button className="btnSeawhite" onClick={onCancel}>
            Cancel
          </Button>
          <h2 className="recommendations-tab-card__title">
            {mode === "create" ? "Add Recommendation" : "Edit Recommendation"}
          </h2>
        </div>
        <div className="recommendations-tab-card__actions">
          <Button
            className="squareGreenButton"
            disabled={!valid}
            onClick={() => valid && onSave(values)}
          >
            Save
          </Button>
        </div>
      </div>

      <ItemPicker
        itemType={values.itemType}
        itemId={values.itemId}
        services={services}
        governances={governances}
        products={products}
        onItemTypeChange={(t: ItemType) =>
          setValues((prev) => ({
            ...prev,
            itemType: t,
            itemId: "",
            refinements: {},
          }))
        }
        onItemIdChange={(id) => set("itemId", id)}
      />

      <div className="row mt-2">
        <div className="col-sm-4">
          <RequiredReactSelect
            label="Quantity"
            value={operandOptions.find((o) => o.value === values.quantityValue)}
            onChange={(opt: any) => set("quantityValue", opt?.value ?? "")}
            options={operandOptions}
            placeholder="Survey value or calculation"
            required
          />
        </div>
      </div>

      <ConditionFields
        value={{
          conditionType: values.conditionType,
          conditionalValue: values.conditionalValue,
          comparisonValue: values.comparisonValue,
          comparisonValueType: values.comparisonValueType,
        }}
        operands={operands}
        onChange={(patch: Partial<ConditionValue>) =>
          setValues((prev) => ({ ...prev, ...patch }))
        }
      />

      {values.itemType === "Task" &&
        values.itemId !== "" &&
        subservices.length > 0 && (
          <RefinementsEditor
            subservices={subservices}
            operands={operands}
            value={values.refinements}
            onChange={(next) => set("refinements", next)}
          />
        )}
    </div>
  );
};

export default RecommendationForm;
